import { z } from 'zod';

const settingFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  type: z.enum(['string', 'email', 'integer', 'enum', 'timezone', 'secret']),
  value: z.union([z.string(), z.number(), z.null()]),
  options: z.array(z.string()),
  source: z.string(),
  secret: z.boolean(),
  configured: z.boolean(),
  lastFour: z.string().nullable(),
  immediate: z.boolean(),
  editable: z.boolean(),
});

export type SettingField = z.infer<typeof settingFieldSchema>;

const categorySchema = z.object({
  id: z.string(),
  label: z.string(),
  settings: z.array(settingFieldSchema),
});

export type SettingsCategory = z.infer<typeof categorySchema>;

const infrastructureSchema = z.object({
  currency: z.string(),
  twoFactorRequired: z.boolean(),
  appUrl: z.string(),
  appEnv: z.string(),
  tenantBaseDomain: z.string(),
  platformHosts: z.array(z.string()),
  defaultAgencySlug: z.string(),
  filesystemDisk: z.string(),
  queue: z.string(),
  cache: z.string(),
  mailDriver: z.string(),
  sessionLifetimeMinutes: z.number(),
  sessionSecureCookie: z.boolean().nullable(),
  unsupported: z.array(z.string()),
});

export type PlatformInfrastructure = z.infer<typeof infrastructureSchema>;

const settingsPayloadSchema = z.object({
  categories: z.array(categorySchema),
  mailOverride: z.boolean(),
  infrastructure: infrastructureSchema,
});

export type PlatformSettingsPayload = z.infer<typeof settingsPayloadSchema>;

const healthSchema = z.object({
  database: z.string(),
  queue: z.string(),
  cache: z.string(),
  mail: z.object({ driver: z.string(), override: z.boolean() }),
  sms: z.object({ provider: z.string(), configured: z.boolean() }),
  ai: z.object({ provider: z.string().nullable(), configured: z.boolean() }),
  storage: z.object({ disk: z.string() }),
});

export type PlatformHealth = z.infer<typeof healthSchema>;

const changeSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  actorName: z.string(),
  createdAt: z.string(),
  reason: z.string().nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
});

export type SettingChange = z.infer<typeof changeSchema>;

export function parsePlatformSettings(payload: unknown): PlatformSettingsPayload {
  const parsed = settingsPayloadSchema.safeParse(payload);
  if (!parsed.success) throw new Error('Platform settings could not be read.');
  return parsed.data;
}

export function parsePlatformHealth(payload: unknown): PlatformHealth {
  const parsed = healthSchema.safeParse(payload);
  if (!parsed.success) throw new Error('Platform health could not be read.');
  return parsed.data;
}

export function parseSettingChanges(payload: unknown): SettingChange[] {
  const record = z.object({ changes: z.array(changeSchema) }).safeParse(payload);
  if (!record.success) throw new Error('Settings history could not be read.');
  return record.data.changes;
}

export function fieldInput(field: SettingField): string {
  if (field.secret || field.value === null) return '';
  return String(field.value);
}
